/**
 * ThreadRowActions — a row's ⋯ menu and its inline rename (2026-09-09): from
 * ANY row on the rail or the sheet — rename (the title becomes a field), move
 * to… (every section, the built-ins included, in the same dark menu), pin,
 * archive. One gesture, never "open the thread first".
 */
import React, { useRef } from 'react';
import { createPortal } from 'react-dom';
import { CategoryMoveList } from './CategoryMoveMenu';
import { threadListCopy } from './threadListCopy';
import { useAnchoredMenu, type ThreadOrganizeApi } from './threadOrganize';
import { RailGhostIconButton, RailInlineInput, RailMenu, RailMenuItem } from './organizeStyles';
import type { ChatThread } from './types';
import { useInlineRenameField } from './useInlineRenameField';
import { THREAD_NAME_MAX_LEN } from '../../services/groupThreads/canonicalThreadTitle';

const Dots: React.FC = () => (
  <svg viewBox="0 0 12 12" aria-hidden="true">
    <circle cx="2.5" cy="6" r="1.2" fill="currentColor" />
    <circle cx="6" cy="6" r="1.2" fill="currentColor" />
    <circle cx="9.5" cy="6" r="1.2" fill="currentColor" />
  </svg>
);

export interface ThreadRowActionsProps {
  thread: ChatThread;
  displayTitle: string;
  organize: ThreadOrganizeApi;
  onStartRename: () => void;
  onTogglePin?: (threadId: string) => void;
  onArchive?: (threadId: string) => void;
}

export const ThreadRowActions: React.FC<ThreadRowActionsProps> = ({ thread, displayTitle, organize, onStartRename, onTogglePin, onArchive }) => {
  const copy = threadListCopy(organize.lang);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menu = useAnchoredMenu(buttonRef);
  const [moveOpen, setMoveOpen] = React.useState(false);
  const current = organize.categories.find((c) => c.id === (organize.foldAliases[thread.category ?? ''] ?? thread.category)) ?? null;
  const close = (refocus: boolean) => {
    setMoveOpen(false);
    menu.close(refocus);
  };
  return (
    <>
      <RailGhostIconButton
        ref={buttonRef}
        type="button"
        data-thread-row-menu-btn=""
        aria-haspopup="menu"
        aria-expanded={menu.open}
        aria-label={copy.threadActions(displayTitle)}
        title={copy.threadActions(displayTitle)}
        tabIndex={-1}
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); setMoveOpen(false); menu.toggle(); }}
      >
        <Dots />
      </RailGhostIconButton>
      {menu.open && typeof document !== 'undefined' && createPortal(
        <RailMenu
          ref={menu.menuRef}
          role="menu"
          aria-label={copy.threadActions(displayTitle)}
          data-thread-row-menu=""
          data-prest0n-portal="true"
          $left={menu.pos.left}
          $top={menu.pos.top}
          onClick={(e) => e.stopPropagation()}
        >
          {moveOpen ? (
            <CategoryMoveList
              dark
              lang={organize.lang}
              categories={organize.categories}
              currentCategoryId={current?.id ?? null}
              naturalSectionId={organize.naturalSectionIdFor(thread)}
              onMoveToCategory={(id) => organize.moveThread(thread.id, id)}
              onCreateCategory={organize.createCategory}
              threadTitle={displayTitle}
              onDone={() => close(false)}
            />
          ) : (
            <>
              <RailMenuItem type="button" role="menuitem" data-thread-row-action="rename" onClick={() => { close(false); onStartRename(); }}>{copy.rename}</RailMenuItem>
              <RailMenuItem type="button" role="menuitem" data-thread-row-action="move" aria-haspopup="menu" onClick={() => setMoveOpen(true)}>{copy.moveTo('')}</RailMenuItem>
              {onTogglePin && (
                <RailMenuItem type="button" role="menuitem" data-thread-row-action="pin" onClick={() => { onTogglePin(thread.id); close(true); }}>{thread.pinned ? copy.unpin : copy.pin}</RailMenuItem>
              )}
              {onArchive && (
                <RailMenuItem type="button" role="menuitem" data-thread-row-action="archive" onClick={() => { close(false); onArchive(thread.id); }}>{copy.archive}</RailMenuItem>
              )}
            </>
          )}
        </RailMenu>,
        document.body,
      )}
    </>
  );
};

export interface ThreadRowRenameInputProps {
  initial: string;
  ariaLabel: string;
  onCommit: (title: string) => void;
  onCancel: () => void;
}

/**
 * The row title's inline rename field: Enter / a real blur commit, Escape
 * cancels; never reaches the row button.
 *
 * `initial` is the DISPLAYED title, so the name a thread took from its summary
 * is what you edit — and since 2026-09-16 it keeps the same typing contract the
 * section fields do (`useInlineRenameField`): a system character palette does
 * not commit it half-typed, a dead key or an IME finishes composing before
 * Enter counts, and the 42 is a bound in code points so an emoji is never cut
 * in half on its way to storage.
 */
export const ThreadRowRenameInput: React.FC<ThreadRowRenameInputProps> = ({ initial, ariaLabel, onCommit, onCancel }) => {
  const rename = useInlineRenameField({
    initial,
    maxCodePoints: THREAD_NAME_MAX_LEN,
    onCommit: (value) => {
      const clean = value.trim();
      if (clean && clean !== initial) onCommit(clean);
      else onCancel();
    },
    onCancel,
  });
  return (
    <RailInlineInput
      autoFocus
      data-thread-row-rename=""
      aria-label={ariaLabel}
      onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
      onMouseDown={(e) => e.stopPropagation()}
      {...rename.inputProps}
    />
  );
};

export default ThreadRowActions;
